/**
 * Modal — thin wrapper around Radix Dialog with WCA styling.
 * `position` controls where the panel sits (used to switch between the
 * centered ItemModal and the side-anchored CartDrawer).
 */
import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

type Position = 'center' | 'right' | 'bottom';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Required for screen readers. Visually hidden if you don't render your own header. */
  title: string;
  description?: string;
  position?: Position;
  className?: string;
  children: ReactNode;
}

const POSITION_CLASSES: Record<Position, string> = {
  center:
    'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-md rounded-2xl max-h-[90dvh] overflow-hidden',
  right: 'right-0 top-0 h-dvh w-full max-w-md',
  bottom: 'bottom-0 left-0 right-0 w-full max-h-[85dvh] rounded-t-2xl',
};

/** Entrance animation per anchor — gated to `motion-safe` so reduced-motion
 *  users get the panel placed instantly with no movement. */
const ENTER_ANIM: Record<Position, string> = {
  center: 'motion-safe:animate-[cm-modal-in_180ms_ease-out]',
  right: 'motion-safe:animate-[cm-drawer-right_220ms_ease-out]',
  bottom: 'motion-safe:animate-[cm-drawer-bottom_220ms_ease-out]',
};

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  position = 'center',
  className,
  children,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-wca-black/60 backdrop-blur-sm motion-safe:animate-[cm-overlay-in_180ms_ease-out]" />
        <Dialog.Content
          className={cn(
            'fixed z-50 flex flex-col border border-wca-black/[0.06] bg-wca-surface shadow-card-lg outline-none',
            POSITION_CLASSES[position],
            ENTER_ANIM[position],
            className,
          )}
        >
          <Dialog.Title className="sr-only">{title}</Dialog.Title>
          {description !== undefined && (
            <Dialog.Description className="sr-only">{description}</Dialog.Description>
          )}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
